import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { passwordService } from '../services/passwordService';

// PasswordContext 생성
const PasswordContext = createContext();

// 커스텀 훅: usePassword
export const usePassword = () => {
  const context = useContext(PasswordContext);
  if (!context) {
    throw new Error('usePassword must be used within a PasswordProvider');
  }
  return context;
};

/**
 * PasswordProvider 컴포넌트
 * 패스워드 관리 상태와 기능들을 제공
 */
export const PasswordProvider = ({ children }) => {
  const { user } = useAuth();
  const [passwords, setPasswords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 패스워드 목록 로드
  const loadPasswords = async () => {
    if (!user?.uid) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const passwordList = await passwordService.getPasswords(user.uid);
      setPasswords(passwordList);
    } catch (error) {
      console.error('패스워드 로드 실패:', error);
      setError('패스워드 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 새 패스워드 추가
  const addPassword = async (passwordData) => {
    if (!user?.uid) throw new Error('로그인이 필요합니다.');
    
    try {
      setLoading(true);
      setError(null);
      
      const newPassword = await passwordService.addPassword(user.uid, passwordData);
      
      // 로컬 상태 업데이트
      setPasswords(prev => [newPassword, ...prev]);
      
      return newPassword;
    } catch (error) {
      console.error('패스워드 추가 실패:', error);
      setError('패스워드 추가에 실패했습니다.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 패스워드 수정
  const updatePassword = async (passwordId, updatedData) => {
    if (!user?.uid) throw new Error('로그인이 필요합니다.');
    
    try {
      setLoading(true);
      setError(null);
      
      const updatedPassword = await passwordService.updatePassword(
        user.uid, 
        passwordId, 
        updatedData
      );
      
      // 로컬 상태 업데이트
      setPasswords(prev => 
        prev.map(pwd => 
          pwd.id === passwordId ? updatedPassword : pwd
        )
      );
      
      return updatedPassword;
    } catch (error) {
      console.error('패스워드 수정 실패:', error);
      setError('패스워드 수정에 실패했습니다.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 패스워드 삭제
  const deletePassword = async (passwordId) => {
    if (!user?.uid) throw new Error('로그인이 필요합니다.');
    
    try {
      setLoading(true);
      setError(null);
      
      await passwordService.deletePassword(user.uid, passwordId);
      
      // 로컬 상태 업데이트
      setPasswords(prev => prev.filter(pwd => pwd.id !== passwordId));
      
    } catch (error) {
      console.error('패스워드 삭제 실패:', error);
      setError('패스워드 삭제에 실패했습니다.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 모든 패스워드 삭제
  const deleteAllPasswords = async () => {
    if (!user?.uid) throw new Error('로그인이 필요합니다.');
    
    try {
      setLoading(true);
      setError(null);
      
      const deletedCount = await passwordService.deleteAllPasswords(user.uid);
      
      // 로컬 상태 초기화
      setPasswords([]);
      
      return deletedCount;
    } catch (error) {
      console.error('모든 패스워드 삭제 실패:', error);
      setError('모든 패스워드 삭제에 실패했습니다.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 암호화된 패스워드 가져오기 (import 기능용)
  const importPassword = async (encryptedPasswordData) => {
    if (!user?.uid) throw new Error('로그인이 필요합니다.');
    
    try {
      setLoading(true);
      setError(null);
      
      const importedPassword = await passwordService.importPassword(user.uid, encryptedPasswordData);
      
      // 복호화에 성공한 경우만 로컬 상태에 추가
      if (!importedPassword.error) {
        setPasswords(prev => [importedPassword, ...prev]);
      }
      
      return importedPassword;
    } catch (error) {
      console.error('패스워드 가져오기 실패:', error);
      setError('패스워드 가져오기에 실패했습니다.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 패스워드 검색
  const searchPasswords = (searchTerm) => {
    if (!searchTerm.trim()) return passwords;
    
    const term = searchTerm.toLowerCase();
    return passwords.filter(password => 
      password.title.toLowerCase().includes(term) ||
      password.username.toLowerCase().includes(term) ||
      password.website?.toLowerCase().includes(term)
    );
  };

  // 에러 초기화
  const clearError = () => {
    setError(null);
  };

  // 사용자 변경시 패스워드 목록 초기화 및 로드
  useEffect(() => {
    if (user?.uid) {
      loadPasswords();
    } else {
      // 로그아웃시 상태 초기화
      setPasswords([]);
      setError(null);
    }
  }, [user?.uid]);

  // Context 값
  const value = {
    // 상태
    passwords,
    loading,
    error,
    
    // 액션
    loadPasswords,
    refreshPasswords: loadPasswords, // 새로고침용 별칭
    addPassword,
    updatePassword,
    deletePassword,
    deleteAllPasswords,
    importPassword,
    searchPasswords,
    clearError,
    
    // 계산된 값
    passwordCount: passwords.length,
    isEmpty: passwords.length === 0
  };

  return (
    <PasswordContext.Provider value={value}>
      {children}
    </PasswordContext.Provider>
  );
};

export default PasswordContext;
export { PasswordContext };